import type {PropsWithChildren, ReactNode} from 'react';
import React, { useCallback, useMemo} from 'react';
import type {
  DataGridProProps,
  GridColDef,
  GridColumnOrderChangeParams,
  GridColumnResizeParams,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridRowParams,
  GridValidRowModel} from '@mui/x-data-grid-pro';
import {
  DataGridPro,
  gridColumnFieldsSelector,
  gridColumnResizeSelector,
  gridColumnVisibilityModelSelector,
} from '@mui/x-data-grid-pro';
import type {GridApiPro} from '@mui/x-data-grid-pro/models/gridApiPro';
import type {GridRowSelectionModel} from '@mui/x-data-grid/models/gridRowSelectionModel';
import type {
  FilterModel,
  PaginationModel,
  RowId,
  RowSelectionMode,
  Sorting,
} from './types';
import {CustomFooter, CustomGridToolbar, NoRowsOverlay} from './components';
import {
  compareVisibilityModels,
  filterPanelProps,
  getTableStyles,
} from './functions';
import {useSorting} from './hooks';

export type DataTableProps<TData extends GridValidRowModel> = {
  apiRef: React.RefObject<GridApiPro | null>;
  className?: string;
  loading?: boolean;
  columns: GridColDef[];
  rows: TData[];
  sorting?: Sorting;
  enableSelectionButton?: RowSelectionMode;
  selectionModel?: GridRowSelectionModel;
  onSelectionChanged?: (model: GridRowSelectionModel) => void;
  onSortingChanged?: (sorting?: Sorting) => void;
  onRowClick?: (row: TData) => void;
  filterModel?: FilterModel;
  setFilterModel?: (model: FilterModel) => void;
  paginationModel?: PaginationModel;
  setPaginationModel?: (model: PaginationModel) => void;
  setColumnVisibility?: (columnName: string, visible: boolean) => void;
  setColumnsOrder?: (columnsOrder: string[]) => void;
  setColumnWidth?: (columnName: string, width: number) => void;
  getRowHeight?: (params: {
    id: RowId;
    densityFactor: number;
  }) => number | undefined | 'auto';
  header?: ReactNode;
  columnSelectionTitle?: string;
  pageSizeAllowAll?: boolean;
  onRowsUpdate?: (gridRows: GridValidRowModel[]) => void;
  scrollAfterPagination?: boolean;
} & Pick<
  DataGridProProps<TData>,
  | 'hideFooter'
  | 'slots'
  | 'slotProps'
  | 'columnVisibilityModel'
  | 'pinnedColumns'
  | 'rowCount'
>;

/**
 * https://mui.com/x/react-data-grid/layout/#flex-parent-container
 */
const tableSx: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  minHeight: '175px',
};

export function DataTable<TData extends GridValidRowModel>({
  apiRef,
  className,
  loading,
  columns,
  rows,
  sorting,
  enableSelectionButton,
  selectionModel,
  onSortingChanged,
  onSelectionChanged,
  onRowClick,
  setColumnsOrder,
  setColumnWidth,
  columnVisibilityModel,
  setColumnVisibility,
  filterModel,
  setFilterModel,
  getRowHeight,
  slots,
  slotProps,
  header,
  columnSelectionTitle,
  pageSizeAllowAll,
  hideFooter,
  pinnedColumns,
  onRowsUpdate,
  paginationModel,
  setPaginationModel,
  rowCount,
  scrollAfterPagination,
}: PropsWithChildren<DataTableProps<TData>>) {
  const {sortModel, handleToggleSort} = useSorting(sorting, onSortingChanged);

  const handleSelectionChanged = useCallback(
    (model: GridRowSelectionModel) => {
      if (onSelectionChanged) {
        onSelectionChanged(model);
      }
    },
    [onSelectionChanged],
  );

  const handleColumnOrderChange = useCallback(
    (_params: GridColumnOrderChangeParams) => {
      const newOrder = gridColumnFieldsSelector(apiRef);

      if (setColumnsOrder) {
        setColumnsOrder(newOrder);
      }

      return newOrder;
    },
    [apiRef, setColumnsOrder],
  );

  const handleColumnWidthChange = useCallback(
    (params: GridColumnResizeParams) => {
      const columnResizeState = gridColumnResizeSelector(apiRef);

      if (setColumnWidth) {
        setColumnWidth(params.colDef.field, params.width);
      }

      return columnResizeState;
    },
    [apiRef, setColumnWidth],
  );

  const handleColumnVisibilityChange = useCallback(
    (newModel: GridColumnVisibilityModel) => {
      const columnVisibilityState = gridColumnVisibilityModelSelector(apiRef);

      if (setColumnVisibility) {
        const diff = compareVisibilityModels(columnVisibilityState, newModel);
        Object.keys(diff).forEach(key => {
          setColumnVisibility(key, diff[key]);
        });
      }

      return newModel;
    },
    [apiRef, setColumnVisibility],
  );

  const handleFilterModelChange = useCallback(
    (model: GridFilterModel) => {
      if (setFilterModel) {
        setFilterModel(model);
      }

      return model;
    },
    [setFilterModel],
  );

  const handleRowClick = useCallback(
    (params: GridRowParams) => {
      onRowClick && onRowClick(params.row as TData);
    },
    [onRowClick],
  );

  const sx = useMemo(() => {
    return getTableStyles<TData>(onRowClick, getRowHeight);
  }, [getRowHeight, onRowClick]);

  const pageSizeOptions = useMemo(() => {
    const allOption = -1;
    const allowAllGreaterThanLimit = pageSizeAllowAll ? [allOption] : [];

    return [20, 50, 100, 200, ...allowAllGreaterThanLimit];
  }, [pageSizeAllowAll]);

  return (
    <div style={tableSx}>
      <DataGridPro<TData>
        apiRef={apiRef}
        pagination={true}
        paginationMode={rowCount !== undefined ? 'server' : 'client'}
        initialState={{
          pagination: {
            rowCount,
            paginationModel: paginationModel,
          },
        }}
        rowCount={rowCount}
        pinnedColumns={pinnedColumns}
        className={className}
        loading={loading}
        columns={columns}
        rows={rows}
        sx={sx}
        density="compact"
        rowSpacingType="margin"
        getRowHeight={getRowHeight}
        rowSelectionPropagation={{parents: false, descendants: false}}
        hideFooter={hideFooter}
        showToolbar
        sortModel={sortModel}
        onColumnHeaderClick={handleToggleSort}
        rowSelection={
          enableSelectionButton === 'single' ||
          enableSelectionButton === 'multiple'
        }
        rowSelectionModel={selectionModel}
        disableMultipleRowSelection={enableSelectionButton === 'single'}
        disableRowSelectionOnClick={true}
        onRowSelectionModelChange={handleSelectionChanged}
        checkboxSelection={
          enableSelectionButton === 'single' ||
          enableSelectionButton === 'multiple'
        }
        onRowClick={handleRowClick}
        disableColumnResize={setColumnWidth === undefined}
        filterModel={filterModel}
        onFilterModelChange={handleFilterModelChange}
        paginationModel={paginationModel}
        /*
         A bug in MUI DataGrid pagination prevent override of the paginator,
         so we apply the changes directly instead of using onPaginationModelChange.
         @see https://github.com/mui/mui-x/issues/17505
        */
        onColumnOrderChange={handleColumnOrderChange}
        onColumnWidthChange={handleColumnWidthChange}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={handleColumnVisibilityChange}
        disableColumnMenu={true}
        pageSizeOptions={pageSizeOptions}
        slots={{
          noRowsOverlay: NoRowsOverlay,
          toolbar: CustomGridToolbar,
          footer: CustomFooter,
          ...slots,
        }}
        slotProps={{
          ...slotProps,
          toolbar: {
            setPaginationModel,
            header: header,
            columnSelectionTitle,
            onRowsUpdate,
            scrollAfterPagination,
            ...slotProps?.toolbar,
          },
          footer: {
            setPaginationModel,
            scrollAfterPagination,
          },
          basePopper: {
            placement: 'bottom-end',
          },
          filterPanel: filterPanelProps,
        }}
      />
    </div>
  );
}
